import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Popover } from '@opendoor/atoms/popover';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { PopoverRadioOptions } from 'components/marketplace/shared/PopoverRadioOptions';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

interface DirectoryControlsProps {
  availableCities?: Array<{ name: string; slug: string }> | null;
  className?: string;
  locationSlug: string;
  marketSlug?: string;
}

export const DirectoryControls: React.FC<DirectoryControlsProps> = ({
  availableCities,
  className,
  locationSlug,
  marketSlug,
}) => {
  const [isCityOpen, setIsCityOpen] = useState(false);

  const hasMultipleCities = availableCities != null && availableCities.length > 1;

  const cityOptions =
    availableCities?.map((city) => ({ label: city.name, value: city.slug })) ?? [];
  const selectedCity = cityOptions.find((option) => option.value === marketSlug) ?? cityOptions[0];

  const renderCityTrigger: PopoverTriggerRender = (props) => (
    <Button
      {...props}
      analyticsName="directory-city-select"
      aria-label={`Top homes in: ${selectedCity?.label ?? ''}`}
      size="sm"
      variant="secondary"
      className="h-10 w-full min-w-0 justify-between rounded-2xl px-3"
    >
      <Text as="span" className="whitespace-nowrap text-body-sm text-od-content-primary">
        {selectedCity?.label}
      </Text>
      <IconChevronDown size={16} aria-hidden />
    </Button>
  );

  return (
    <Row className={cn('items-center justify-center gap-2 lg:gap-4', className)}>
      {hasMultipleCities && (
        <Box className="min-w-38 flex-1 md:w-65 md:flex-none">
          <Popover.Root open={isCityOpen} onOpenChange={setIsCityOpen}>
            <Popover.Trigger render={renderCityTrigger} />
            <Popover
              align="end"
              size="sm"
              className={cn('w-56', MARKETPLACE_POPOVER_SURFACE_CLASS)}
            >
              <Popover.Title className="sr-only">Top homes in</Popover.Title>
              <PopoverRadioOptions
                aria-label="Top homes in"
                analyticsName="directory-city-select"
                options={cityOptions}
                value={selectedCity?.value ?? ''}
                onValueChange={(slug) => {
                  setIsCityOpen(false);
                  window.location.href = getCustomerNavigationHref(
                    `/homes/state/${locationSlug}?city=${slug}`,
                  );
                }}
              />
            </Popover>
          </Popover.Root>
        </Box>
      )}
      <Button
        analyticsName="see-map-link"
        className="h-10"
        href={`/homes/${marketSlug}`}
        size="sm"
        variant="primary"
        aria-label="View Map"
      >
        View Map
      </Button>
    </Row>
  );
};

interface DirectoryHeaderProps {
  locationName: string;
  locationSlug: string;
  availableCities?: Array<{ name: string; slug: string }> | null;
  marketSlug?: string;
}

export const DirectoryHeader: React.FC<DirectoryHeaderProps> = ({
  locationName,
  locationSlug,
  availableCities,
  marketSlug,
}) => (
  <Col className="sticky top-0 z-[1] bg-od-bg-primary py-2">
    <Row className="items-center justify-between gap-4">
      <Text as="h1" className="text-header-sm pb-1 md:text-header-lg">
        Homes for Sale in {locationName}
      </Text>
      <DirectoryControls
        availableCities={availableCities}
        className="hidden md:flex"
        locationSlug={locationSlug}
        marketSlug={marketSlug}
      />
    </Row>
  </Col>
);
